/**
 * The teams of one space (RESTORE_PLAN R1.5), from the pre-November TeamsList, TeamDialog and
 * ProjectDetailView: a card per team with its description and how many request types it owns;
 * add, edit (one team at a time, Save only when changed) and delete; open a team to see its
 * request types and open one in the request-type builder. Also the readiness summary from the
 * old ProjectsList (teams, workflows, work types, "ready for implementation").
 */
import { useEffect, useState } from 'react';
import { AlertCircle, ArrowLeft, Briefcase, CheckCircle2, ChevronRight, Edit, GitBranch, Loader2, Plus, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RequestTypeFormBuilder } from './RequestTypeFormBuilder';
import {
  readinessText,
  requestTypeCountLabel,
  requestTypesForTeam,
  teamDetailsOf,
  teamNameTaken,
  type SpaceReadiness,
  type SpaceTeamsShape,
  type TeamDetail,
  type TeamRequestTypeRef,
} from '../utils/spaceTeams';
import { statusLabel } from '../utils/requestTypeHistory';
import { addSpaceTeam, editSpaceTeam, removeSpaceTeam } from '../utils/spaceTeamsApi';

export function SpaceReadinessSummary({ readiness, compact = false }: { readiness: SpaceReadiness | null | undefined; compact?: boolean }) {
  if (!readiness) return null;
  const text = readinessText(readiness);
  const counts = [
    { label: 'Teams', value: readiness.teams, icon: Users },
    { label: 'Workflows', value: readiness.workflows, icon: GitBranch },
    { label: 'Work Types', value: readiness.workTypes, icon: Briefcase },
  ];
  return (
    <div className="space-y-2" data-testid="space-readiness">
      {readiness.applicable && (
        <div className={compact ? 'flex items-center gap-4 text-sm text-gray-600' : 'grid grid-cols-3 gap-3'}>
          {counts.map(({ label, value, icon: Icon }) => (
            compact ? (
              <span key={label} className="flex items-center gap-1" title={label}>
                <Icon className="size-4" aria-hidden />
                <span>{value}</span>
                <span className="sr-only">{label}</span>
              </span>
            ) : (
              <div key={label} className="flex items-center gap-2">
                <Icon className="size-4 text-muted-foreground" aria-hidden />
                <div>
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p>{value}</p>
                </div>
              </div>
            )
          ))}
        </div>
      )}
      {text && (
        <p className={`flex items-center gap-1.5 text-sm ${readiness.ready ? 'text-green-700' : 'text-orange-700'}`} data-testid="space-readiness-text">
          {readiness.ready ? <CheckCircle2 className="size-4" aria-hidden /> : <AlertCircle className="size-4" aria-hidden />}
          {text}
        </p>
      )}
    </div>
  );
}

interface SpaceForTeams extends SpaceTeamsShape {
  id: string;
  companyId: string;
  name: string;
}

interface SpaceTeamsPanelProps {
  space: SpaceForTeams;
  requestTypes: TeamRequestTypeRef[];
  accessToken: string;
  /** False for a client's read-only view: no add, edit, delete. */
  canEdit?: boolean;
  /** After a team or request type changes: reload the space and the overview. */
  onChanged: (space?: Record<string, unknown>) => void | Promise<void>;
}

export function SpaceTeamsPanel({ space, requestTypes, accessToken, canEdit = true, onChanged }: SpaceTeamsPanelProps) {
  const teams = teamDetailsOf(space);
  const [dialog, setDialog] = useState<{ mode: 'add' } | { mode: 'edit'; team: TeamDetail } | null>(null);
  const [openTeam, setOpenTeam] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [builderFor, setBuilderFor] = useState<TeamRequestTypeRef | null>(null);

  const selected = openTeam ? teams.find((t) => t.name === openTeam) || null : null;

  const handleDelete = async (team: TeamDetail) => {
    const { owned } = requestTypesForTeam(team.name, requestTypes);
    const note = owned.length > 0 ? ` Its ${requestTypeCountLabel(owned.length)} will move under "No team".` : '';
    if (!window.confirm(`Delete team "${team.name}" from ${space.name}?${note}`)) return;
    try {
      setDeleting(team.name);
      const { space: saved } = await removeSpaceTeam(space.companyId, space.id, team.name, accessToken);
      toast.success(`Team "${team.name}" deleted`);
      if (openTeam === team.name) setOpenTeam(null);
      await onChanged(saved);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete the team');
    } finally {
      setDeleting(null);
    }
  };

  if (selected) {
    const { owned, restricted } = requestTypesForTeam(selected.name, requestTypes);
    return (
      <div className="space-y-4" data-testid="space-team-detail">
        <Button variant="ghost" size="sm" onClick={() => setOpenTeam(null)}>
          <ArrowLeft className="size-4 mr-2" />
          Back to teams
        </Button>
        <div>
          <h3 className="flex items-center gap-2">
            <Users className="size-5 text-muted-foreground" aria-hidden />
            {selected.name}
          </h3>
          {selected.description && <p className="text-sm text-muted-foreground mt-1">{selected.description}</p>}
          <p className="text-sm text-muted-foreground mt-1">{requestTypeCountLabel(owned.length)}</p>
        </div>
        <TeamRequestTypeList title="Request types this team owns" items={owned} empty="No request types are owned by this team yet. Set the owning team in the request-type builder." onOpen={setBuilderFor} />
        {restricted.length > 0 && (
          <TeamRequestTypeList title="Also open to this team" items={restricted} empty="" onOpen={setBuilderFor} />
        )}
        <RequestTypeBuilderDialog
          requestType={builderFor}
          companyId={space.companyId}
          accessToken={accessToken}
          onClose={() => setBuilderFor(null)}
          onSaved={async () => {
            setBuilderFor(null);
            await onChanged();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="space-teams">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Teams working in this space. Each team owns its request types; a request type can also be opened to other teams.
        </p>
        {canEdit && (
          <Button size="sm" onClick={() => setDialog({ mode: 'add' })}>
            <Plus className="size-4 mr-2" />
            Add Team
          </Button>
        )}
      </div>

      {teams.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Users className="size-10 mx-auto mb-3 text-gray-400" aria-hidden />
            <p className="font-medium">No teams yet</p>
            <p className="text-sm text-muted-foreground mt-1">Add teams to this space to start creating request types for them.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {teams.map((team) => {
            const { owned } = requestTypesForTeam(team.name, requestTypes);
            return (
              <Card key={team.name} className="hover:border-primary/50 transition-colors" data-testid="space-team-card">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <button
                      type="button"
                      className="flex-1 text-left"
                      onClick={() => setOpenTeam(team.name)}
                      aria-label={`Open team ${team.name}`}
                    >
                      <p className="font-medium flex items-center gap-1">
                        {team.name}
                        <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                      </p>
                      {team.description && <p className="text-sm text-muted-foreground mt-1">{team.description}</p>}
                      <p className="text-sm text-muted-foreground mt-2" data-testid="space-team-count">{requestTypeCountLabel(owned.length)}</p>
                    </button>
                    {canEdit && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" aria-label={`Edit team ${team.name}`} onClick={() => setDialog({ mode: 'edit', team })}>
                          <Edit className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Delete team ${team.name}`}
                          disabled={deleting === team.name}
                          onClick={() => handleDelete(team)}
                        >
                          {deleting === team.name ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4 text-destructive" />}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <SpaceTeamDialog
        open={!!dialog}
        team={dialog?.mode === 'edit' ? dialog.team : null}
        teams={teams}
        onClose={() => setDialog(null)}
        onSave={async (value) => {
          const result = dialog?.mode === 'edit'
            ? await editSpaceTeam(space.companyId, space.id, dialog.team.name, value, accessToken)
            : await addSpaceTeam(space.companyId, space.id, value, accessToken);
          const moved = 'requestTypesUpdated' in result ? Number(result.requestTypesUpdated) || 0 : 0;
          toast.success(dialog?.mode === 'edit'
            ? `Team "${value.name}" updated${moved > 0 ? `; ${requestTypeCountLabel(moved)} updated` : ''}`
            : `Team "${value.name}" added`);
          setDialog(null);
          await onChanged(result.space);
        }}
      />
    </div>
  );
}

function TeamRequestTypeList({ title, items, empty, onOpen }: { title: string; items: TeamRequestTypeRef[]; empty: string; onOpen: (rt: TeamRequestTypeRef) => void }) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium">{title}</h4>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y rounded-md border" data-testid="space-team-request-types">
          {items.map((rt) => {
            const fieldCount = Array.isArray(rt.fields) ? rt.fields.length : 0;
            return (
              <li key={rt.id} className="flex items-center justify-between gap-3 p-3">
                <div>
                  <p className="font-medium">{rt.requestTypeName || 'Untitled request type'}</p>
                  <p className="text-xs text-muted-foreground">{fieldCount} {fieldCount === 1 ? 'field' : 'fields'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{statusLabel(rt.status).label}</Badge>
                  <Button variant="outline" size="sm" onClick={() => onOpen(rt)} aria-label={`Open ${rt.requestTypeName || 'request type'}`}>
                    Open
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RequestTypeBuilderDialog({ requestType, companyId, accessToken, onClose, onSaved }: {
  requestType: TeamRequestTypeRef | null;
  companyId: string;
  accessToken: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  return (
    <Dialog open={!!requestType} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="sm:max-w-7xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Request Type</DialogTitle>
          <DialogDescription>Your progress is automatically saved.</DialogDescription>
        </DialogHeader>
        {requestType && (
          <RequestTypeFormBuilder
            serviceProjectId={String(requestType.serviceProjectId || '')}
            requestType={requestType}
            companyId={companyId}
            sowId={typeof requestType.sowId === 'string' ? requestType.sowId : undefined}
            accessToken={accessToken}
            onSave={async () => { await onSaved(); }}
            onCancel={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Add or edit one team (TeamDialog): name and description; Save only when something changed. */
export function SpaceTeamDialog({ open, team, teams, onClose, onSave }: {
  open: boolean;
  team: TeamDetail | null;
  teams: TeamDetail[];
  onClose: () => void;
  onSave: (value: TeamDetail) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(team?.name || '');
    setDescription(team?.description || '');
  }, [open, team]);

  const taken = !!name.trim() && teamNameTaken(teams, name, team?.name);
  const changed = team ? name.trim() !== team.name || description.trim() !== team.description : true;
  const canSave = !!name.trim() && !taken && changed && !saving;

  const save = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      await onSave({ name: name.trim(), description: description.trim() });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the team');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{team ? 'Edit Team' : 'Add New Team'}</DialogTitle>
          <DialogDescription>
            {team
              ? 'Update the team. A new name is carried into the request types that use it.'
              : 'Create a new team within this space. Teams own their request types, and a request type can also be opened to other teams.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="space-team-name">Team Name *</Label>
            <Input
              id="space-team-name"
              placeholder="e.g., HR, IT Support, Facilities"
              value={name}
              aria-invalid={taken}
              onChange={(e) => setName(e.target.value)}
            />
            <p className={`text-xs ${taken ? 'text-destructive' : 'text-muted-foreground'}`}>
              {taken ? 'This space already has a team with this name.' : 'The name of the team working in this space'}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="space-team-description">Description (Optional)</Label>
            <Textarea
              id="space-team-description"
              placeholder="Brief description of this team's responsibilities"
              value={description}
              rows={3}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={!canSave}>
            {saving && <Loader2 className="size-4 mr-2 animate-spin" />}
            {team ? 'Save Changes' : 'Add Team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
